import { Popover, PopoverTrigger, PopoverContent } from "@components/common/popover"
import { Button } from "@components/common/button"
import useTranslator from "@pma/hooks/use-translator"

import AddTaskTableColumn from "@pma/components/projects/add-task-table-column"
import { Check, X } from "lucide-react"
import { cn } from "@pma/utils/helpers"
import { MixerHorizontalIcon } from "@radix-ui/react-icons"
import React from "react"
import { useCustomColumns } from "@pma/stores/tasks-store"
import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "@pma/hooks/use-toast"
import { DELETE_CUSTOM_COLUMNS_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { Label } from "@pma/components/common/label"
import { Separator } from "@radix-ui/react-separator"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { Checkbox } from "@pma/components/common/checkbox"

interface DataTableViewOptionsProps<TData> {
  context: string
  projectId?: string
}

export function GroupViewOptions<TData>({
  context,
  projectId = "",
}: DataTableViewOptionsProps<TData>) {
  const { translate, locale } = useTranslator()
  const { setShowColumns, showColumns } = React.useContext(GroupedTasksContext)
  const { customColumns, setCustomColumns } = useCustomColumns()
  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const isHebrew = locale === "he"

  const getColumnTitle = (colId: string) => {
    const find = showColumns.find((val) => val.id === colId)
    let title = ""
    if (find) title = find.title
    else title = colId

    return title
  }
  const deleteCustomColumn = async (columnId: string) => {
    const response = await axios.delete(
      SERVER_URL + DELETE_CUSTOM_COLUMNS_ENDPOINT + `/${columnId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const deleteColumnMutation = useMutation({
    mutationKey: ["delete-custom-column"],
    mutationFn: deleteCustomColumn,
    onSuccess: (data) => {
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
      setCustomColumns(data?._custom_columns)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: translate("Unable to delete column"),
        variant: "error",
      })
    },
  })

  const handleColumnVisibility = (column: any) => {
    const newArr = [...showColumns]
    const find = newArr.find((val) => val.id === column.id)
    if (find) {
      const index = newArr.indexOf(find)
      newArr[index]["show"] = !column.show
      localStorage.setItem("tableColumn", JSON.stringify(newArr))
      setShowColumns(newArr)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='ml-auto hidden h-8 lg:flex'>
          <MixerHorizontalIcon className='mr-2 h-4 w-4' />
          {translate("Columns")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='md:w-[350px]'>
        <Label>{translate("Toggle Columns")}</Label>
        <Separator />
        <div className='grid grid-cols-2 gap-x-4 gap-y-2 mb-2'>
          {showColumns.map((column: any) => {
            return (
              <span
                key={column.id}
                className={cn(
                  "flex cursor-pointer items-center gap-1 text-sm capitalize",
                  column.show ? "rounded-lg border border-primary px-2" : "",
                  isHebrew ? "rtl" : "ltr"
                )}
                onClick={() => handleColumnVisibility(column)}
              >
                <Checkbox checked={column.show} />
                {translate(getColumnTitle(column?.id).replaceAll("_", " "))}
              </span>
            )
          })}
        </div>

        <Separator />
        {context === "tasks" && (
          <div className='rounded-md bg-gray-100 p-1 py-1'>
            <h3 className='text-sm font-semibold'>{translate("Custom Columns")}</h3>
            <div className='flex flex-wrap gap-4 py-1'>
              {customColumns.map((column) => {
                return (
                  <span
                    key={column.id}
                    className='flex cursor-pointer items-center gap-1 rounded-md border p-1 text-sm capitalize'
                  >
                    {translate(column.name)}
                    <div className='flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-white'>
                      <X
                        size={10}
                        onClick={() => {
                          deleteColumnMutation.mutate(column?.id)
                        }}
                      />
                    </div>
                  </span>
                )
              })}
            </div>
            <Separator />

            <div className={cn("flex items-center justify-end py-1", isHebrew ? "rtl" : "ltr")}>
              <AddTaskTableColumn context={context} projectId={projectId} />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
